import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getActiveJobForCustomer } from '../api'

/**
 * Global sync-status provider.
 *
 * Mounted once at the app root (around <Routes>) so it survives page
 * navigation — the poller does not restart every time a page mounts. It
 * derives the active customer from the URL (/customers/:customerId/...) and
 * polls the customer-wide active-job endpoint. TopNav consumes this via
 * useSyncStatus() to render a persistent indicator on every page.
 *
 * Polling cadence: ~4s while a job is running, ~12s when idle (to notice a
 * newly started sync without hammering the API).
 */

const SyncStatusContext = createContext({ job: null, customerId: null, isComplete: false })

export function useSyncStatus() {
  return useContext(SyncStatusContext)
}

function parseCustomerId(pathname) {
  const m = pathname.match(/^\/customers\/([^/]+)/)
  if (!m) return null
  const id = decodeURIComponent(m[1])
  if (id === 'new') return null
  return id
}

export function SyncStatusProvider({ children }) {
  const location = useLocation()
  const customerId = parseCustomerId(location.pathname)

  const [job, setJob] = useState(null)                  // running job data
  const [completedFlash, setCompletedFlash] = useState(null) // brief post-completion state
  const wasRunningRef = useRef(false)
  const timerRef = useRef(null)
  const flashTimerRef = useRef(null)

  useEffect(() => {
    if (!customerId) {
      setJob(null)
      setCompletedFlash(null)
      wasRunningRef.current = false
      if (timerRef.current) clearTimeout(timerRef.current)
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
      return
    }

    let cancelled = false

    async function poll() {
      let delay = 12000
      try {
        const res = await getActiveJobForCustomer(customerId)
        if (cancelled) return

        if (res && res.found && res.status === 'running') {
          wasRunningRef.current = true
          setJob(res)
          setCompletedFlash(null)
          delay = 4000
        } else if (wasRunningRef.current) {
          // We watched it run and it just finished — flash "complete" briefly.
          wasRunningRef.current = false
          setJob(null)
          setCompletedFlash(res && res.found ? res : { status: 'completed' })
          if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
          flashTimerRef.current = setTimeout(() => {
            if (!cancelled) setCompletedFlash(null)
          }, 6000)
        } else {
          // Never saw it running in this session — don't surface a stale completion.
          setJob(null)
        }
      } catch {
        // transient error — keep last known state, try again later
      }
      if (!cancelled) timerRef.current = setTimeout(poll, delay)
    }

    poll()
    return () => {
      cancelled = true
      if (timerRef.current) clearTimeout(timerRef.current)
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    }
  }, [customerId])

  const active = job || completedFlash
  return (
    <SyncStatusContext.Provider value={{ job: active, customerId, isComplete: !job && !!completedFlash }}>
      {children}
    </SyncStatusContext.Provider>
  )
}
